import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import { DataGridItem } from "components/data-grid";
import { SimpleCheckbox } from "components/form/form-checkbox";
import Select from "components/select";
import { StyledTextField } from "components/form/form-textfield";
import withStyles from "components/with-styles";
import styles from "./styles/ScoreboardSection.css.js";

const ScoreboardSection = ({ intl, values, setValue, classes, allStreamSources, errors }) => {
  const handleMixerScoreDefaultChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.default", value);
  };

  const handleMixerScoreTitlerUrlChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.titler_url", value);
  };

  const handleMixerScoreUseTitlerChange = e => {
    const isChecked = e.target.checked;
    setValue("file.mixer.score.use_titler", isChecked);

    if (!isChecked) {
      const currentScore = values.file?.mixer?.score || {};
      const { titler_url, ...restScore } = currentScore;
      setValue("file.mixer.score", restScore);
    }
  };

  const handleIncludeSecondaryScoreboardChange = e => {
    const isChecked = e.target.checked;
    setValue("file.mixer.score.include_secondary", isChecked);
  };

  const handleMixerDrawnAutoChange = e => {
    const isChecked = e.target.checked;
    setValue("file.mixer.score.drawn.auto", isChecked);
  };

  const handleMixerDrawnCfgSrcChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.drawn.cfg_src", value);
  };

  const handleMixerDrawnParamsChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.drawn.params", value);
  };

  const scoreboardCamera = (allStreamSources || []).find(source => source.use === "scoreboard");
  const scoreboardCameraUrl = scoreboardCamera?.public_interface?.admin_url || "";

  const scoreDefault = values.file?.mixer?.score?.default;
  const includeSecondary = values.file?.mixer?.score?.include_secondary;
  const burntSectionActive = scoreDefault === "burnt" || (scoreDefault === "drawn" && includeSecondary);

  React.useEffect(
    () => {
      if (burntSectionActive && scoreboardCameraUrl && !values.file?.mixer?.score?.burnt?.score_cam) {
        setValue("file.mixer.score.burnt.score_cam", scoreboardCameraUrl);
      }
    },
    [burntSectionActive, scoreboardCameraUrl, values.file?.mixer?.score?.burnt?.score_cam, setValue],
  );

  const handleMixerBurntScoreCamChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.burnt.score_cam", value);
  };

  const handleMixerBurntScorePerspectiveChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.burnt.score_perspective", value);
  };

  const handleMixerBurntScoreScaleChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.burnt.score_scale", value);
  };

  const handleMixerBurntMarginChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.burnt.margin", value);
  };

  const handleMixerBurntPlaceChange = e => {
    const { value } = e.target;
    setValue("file.mixer.score.burnt.place", value);
  };

  return (
    <>
      <DataGridItem big>
        <Typography variant="h6" gutterBottom className={classes.title}>
          {intl.formatMessage({ id: "roomTemplate.scoreboard.title" }, { defaultMessage: "Scoreboard Configuration" })}
        </Typography>
        <Typography variant="caption" color="textSecondary" className={classes.instructions}>
          {intl.formatMessage(
            { id: "roomTemplate.scoreboard.instructions" },
            { defaultMessage: "Configure scoreboard display settings for drawn and burnt overlays" },
          )}
        </Typography>
      </DataGridItem>
      <DataGridItem big>
        <div className={classes.selectContainer}>
          <Select
            id="file.mixer.score.default"
            name="file.mixer.score.default"
            label={intl.formatMessage({ id: "roomTemplate.mixer.score.default" }, { defaultMessage: "Default" })}
            value={values.file?.mixer?.score?.default || "drawn"}
            onChange={handleMixerScoreDefaultChange}
            elements={[
              {
                value: "none",
                label: intl.formatMessage({ id: "roomTemplate.mixer.score.none" }, { defaultMessage: "None" }),
              },
              {
                value: "drawn",
                label: intl.formatMessage({ id: "roomTemplate.mixer.score.drawn" }, { defaultMessage: "Drawn" }),
              },
              {
                value: "burnt",
                label: intl.formatMessage({ id: "roomTemplate.mixer.score.burnt" }, { defaultMessage: "Burnt" }),
              },
            ]}
            getValueFromElement={el => el.value}
            getLabelFromElement={el => el.label}
          />
          {values.file?.mixer?.score?.default !== "none" && (
            <SimpleCheckbox
              id="file.mixer.score.include_secondary"
              name="file.mixer.score.include_secondary"
              value="include_secondary"
              checked={values.file?.mixer?.score?.include_secondary || false}
              onChange={handleIncludeSecondaryScoreboardChange}
              label={
                values.file?.mixer?.score?.default === "burnt"
                  ? intl.formatMessage(
                      { id: "roomTemplate.mixer.score.includeDrawn" },
                      { defaultMessage: "Include Drawn Scoreboard" },
                    )
                  : intl.formatMessage(
                      { id: "roomTemplate.mixer.score.includeBurnt" },
                      { defaultMessage: "Include Burnt Scoreboard" },
                    )
              }
            />
          )}
        </div>
        {values.file?.mixer?.score?.default !== "none" && (
          <>
            <div className={classes.fieldContainer}>
              <SimpleCheckbox
                id="file.mixer.score.use_titler"
                name="file.mixer.score.use_titler"
                value="use_titler"
                checked={values.file?.mixer?.score?.use_titler || false}
                onChange={handleMixerScoreUseTitlerChange}
                label={intl.formatMessage(
                  { id: "roomTemplate.mixer.score.useTitler" },
                  { defaultMessage: "Use Titler" },
                )}
              />
            </div>
            {values.file?.mixer?.score?.use_titler && (
              <div className={classes.fieldContainer}>
                <StyledTextField
                  type="text"
                  id="file.mixer.score.titler_url"
                  name="file.mixer.score.titler_url"
                  value={values.file?.mixer?.score?.titler_url || ""}
                  onChange={handleMixerScoreTitlerUrlChange}
                  label={intl.formatMessage(
                    { id: "roomTemplate.mixer.score.titlerUrl" },
                    { defaultMessage: "Titler URL" },
                  )}
                />
              </div>
            )}
          </>
        )}
      </DataGridItem>
      {(values.file?.mixer?.score?.default === "drawn" ||
        (values.file?.mixer?.score?.default === "burnt" && values.file?.mixer?.score?.include_secondary)) && (
        <>
          <DataGridItem big>
            <Typography variant="subtitle1" gutterBottom className={classes.sectionTitle}>
              {intl.formatMessage({ id: "roomTemplate.mixer.drawn.title" }, { defaultMessage: "Drawn Configuration" })}
            </Typography>
            <div className={classes.fieldContainer}>
              <SimpleCheckbox
                id="file.mixer.score.drawn.auto"
                name="file.mixer.score.drawn.auto"
                value="auto"
                checked={values.file?.mixer?.score?.drawn?.auto || false}
                onChange={handleMixerDrawnAutoChange}
                label={intl.formatMessage({ id: "roomTemplate.mixer.drawn.auto" }, { defaultMessage: "Auto" })}
              />
            </div>
            <div className={classes.twoColumnGrid}>
              <StyledTextField
                type="text"
                id="file.mixer.score.drawn.cfg_src"
                name="file.mixer.score.drawn.cfg_src"
                value={values.file?.mixer?.score?.drawn?.cfg_src || ""}
                onChange={handleMixerDrawnCfgSrcChange}
                label={intl.formatMessage({ id: "roomTemplate.mixer.drawn.cfgUrl" }, { defaultMessage: "Config URL" })}
                error={!!errors?.file?.mixer?.score?.drawn?.cfg_src}
                helperText={errors?.file?.mixer?.score?.drawn?.cfg_src?.message || ""}
              />
              <StyledTextField
                type="text"
                id="file.mixer.score.drawn.params"
                name="file.mixer.score.drawn.params"
                value={values.file?.mixer?.score?.drawn?.params || ""}
                onChange={handleMixerDrawnParamsChange}
                label={intl.formatMessage({ id: "roomTemplate.mixer.drawn.params" }, { defaultMessage: "Parameters" })}
              />
            </div>
          </DataGridItem>
        </>
      )}
      {(values.file?.mixer?.score?.default === "burnt" ||
        (values.file?.mixer?.score?.default === "drawn" && values.file?.mixer?.score?.include_secondary)) && (
        <>
          <DataGridItem big>
            <Typography variant="subtitle1" gutterBottom className={classes.sectionTitle}>
              {intl.formatMessage({ id: "roomTemplate.mixer.burnt.title" }, { defaultMessage: "Burnt Configuration" })}
            </Typography>
            <div className={classes.threeColumnGrid}>
              {scoreboardCameraUrl ? (
                <div>
                  <Typography variant="caption" color="textSecondary">
                    {intl.formatMessage({ id: "roomTemplate.mixer.burnt.scoreCam" }, { defaultMessage: "Score Cam" })}
                  </Typography>
                  <Typography variant="body2">{scoreboardCameraUrl}</Typography>
                </div>
              ) : (
                <StyledTextField
                  type="text"
                  id="file.mixer.score.burnt.score_cam"
                  name="file.mixer.score.burnt.score_cam"
                  value={values.file?.mixer?.score?.burnt?.score_cam || ""}
                  onChange={handleMixerBurntScoreCamChange}
                  label={intl.formatMessage(
                    { id: "roomTemplate.mixer.burnt.scoreCam" },
                    { defaultMessage: "Score Cam" },
                  )}
                />
              )}
              <StyledTextField
                type="text"
                id="file.mixer.score.burnt.score_perspective"
                name="file.mixer.score.burnt.score_perspective"
                value={values.file?.mixer?.score?.burnt?.score_perspective || ""}
                onChange={handleMixerBurntScorePerspectiveChange}
                label={intl.formatMessage(
                  { id: "roomTemplate.mixer.burnt.scorePerspective" },
                  { defaultMessage: "Score Perspective" },
                )}
                placeholder="x1:y1:x2:y2:x3:y3:x4:y4"
              />
              <StyledTextField
                type="text"
                id="file.mixer.score.burnt.score_scale"
                name="file.mixer.score.burnt.score_scale"
                value={values.file?.mixer?.score?.burnt?.score_scale || ""}
                onChange={handleMixerBurntScoreScaleChange}
                label={intl.formatMessage(
                  { id: "roomTemplate.mixer.burnt.scoreScale" },
                  { defaultMessage: "Score Scale" },
                )}
                placeholder="100x100"
              />
              <StyledTextField
                type="text"
                id="file.mixer.score.burnt.margin"
                name="file.mixer.score.burnt.margin"
                value={values.file?.mixer?.score?.burnt?.margin || ""}
                onChange={handleMixerBurntMarginChange}
                label={intl.formatMessage({ id: "roomTemplate.mixer.burnt.margin" }, { defaultMessage: "Margin" })}
                placeholder="30"
              />
              <Select
                id="file.mixer.score.burnt.place"
                name="file.mixer.score.burnt.place"
                label={intl.formatMessage({ id: "roomTemplate.mixer.burnt.place" }, { defaultMessage: "Place" })}
                value={values.file?.mixer?.score?.burnt?.place || "TR"}
                onChange={handleMixerBurntPlaceChange}
                elements={[
                  {
                    value: "TR",
                    label: intl.formatMessage(
                      { id: "roomTemplate.mixer.burnt.place.topRight" },
                      { defaultMessage: "Top Right" },
                    ),
                  },
                  {
                    value: "TL",
                    label: intl.formatMessage(
                      { id: "roomTemplate.mixer.burnt.place.topLeft" },
                      { defaultMessage: "Top Left" },
                    ),
                  },
                  {
                    value: "CT",
                    label: intl.formatMessage(
                      { id: "roomTemplate.mixer.burnt.place.centerTop" },
                      { defaultMessage: "Top Center" },
                    ),
                  },
                  {
                    value: "CB",
                    label: intl.formatMessage(
                      { id: "roomTemplate.mixer.burnt.place.centerBottom" },
                      { defaultMessage: "Bottom Center" },
                    ),
                  },
                  {
                    value: "BR",
                    label: intl.formatMessage(
                      { id: "roomTemplate.mixer.burnt.place.bottomRight" },
                      { defaultMessage: "Bottom Right" },
                    ),
                  },
                  {
                    value: "BL",
                    label: intl.formatMessage(
                      { id: "roomTemplate.mixer.burnt.place.bottomLeft" },
                      { defaultMessage: "Bottom Left" },
                    ),
                  },
                ]}
                getValueFromElement={el => el.value}
                getLabelFromElement={el => el.label}
              />
            </div>
          </DataGridItem>
        </>
      )}
    </>
  );
};

ScoreboardSection.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  allStreamSources: PropTypes.array,
  errors: PropTypes.object,
};

export default injectIntl(withStyles(styles)(ScoreboardSection));
